<template>
  <div v-if="list?.length">
    <div class="mb-4 flex items-center justify-between gap-4">
      <div class="font-semibold text-xl text-neutral-950 dark:text-neutral-100">{{ $t('labels.relatedContent', list.length !== 1 ? 2 : 1) }}</div>

      <!-- Navegação: só na horizontal, e só quando há o que rolar -->
      <div v-if="orientation === 'horizontal' && (canScrollLeft || canScrollRight)" class="shrink-0 flex items-center gap-2">
        <button
          type="button"
          class="nav-arrow"
          :disabled="!canScrollLeft"
          :title="$t('button.back')"
          @click="scrollList(-1)"
        >
          <UiIcon icon="chevron-left" size="20" />
        </button>
        <button
          type="button"
          class="nav-arrow"
          :disabled="!canScrollRight"
          :title="$t('button.next')"
          @click="scrollList(1)"
        >
          <UiIcon icon="chevron-right" size="20" />
        </button>
      </div>
    </div>
    <OverlayScrollbarsComponent
      ref="scroller"
      defer
      :options="{ scrollbars: { autoHide: 'leave' } }"
      :events="{ initialized: updateArrows, updated: updateArrows, scroll: updateArrows }"
      :class="{ 'max-h-[800px] pr-4 -mr-4': orientation === 'vertical' }"
    >
      <div :class="{
        'space-y-4': orientation === 'vertical',
        'flex space-x-4': orientation === 'horizontal'
      }">
        <div
          v-for="item in list"
          :key="item.id"
          class="little-card cursor-pointer shrink-0"
          :class="{ '!w-full': orientation === 'vertical' }"
          @click="ls.viewItem(item.id, item.type, section)"
        >
          <img v-if="item.img" :src="item.img" :alt="item.name || ''" />
          <div v-else class="fallback-cover p-4">
            <div class="space-y-2">
              <div class="line-clamp-2 font-bold text-lg leading-5 mb-2 break-all">{{ item.name }}</div>
              <div class="line-clamp-2 text-xs font-light break-all">{{ item.description || $t('labels.noDescription') }}</div>
            </div>
          </div>

          <div class="absolute top-0 left-0 w-full">
          </div>

          <div v-if="item.workload" class="absolute bottom-2 right-2 bg-white/50 px-1 py-0.5 rounded-md text-xs font-medium text-black">
            {{ secondsToTime(item.workload) }}
          </div>

          <div v-if="item.progress" class="absolute w-full bottom-0 left-0">
            <UiProgress :percent="item.progress" :height="4" type="danger" no-text class="" />
          </div>
        </div>
      </div>
    </OverlayScrollbarsComponent>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useLearningStore } from '@/stores/learningStore'
import { OverlayScrollbarsComponent } from 'overlayscrollbars-vue'
import 'overlayscrollbars/overlayscrollbars.css'

import UiIcon from '@/components/ui/icon.vue'
import UiProgress from '@/components/ui/progress.vue'
// import CardTag from './components/cardTag.vue'

import type { OverlayScrollbarsComponentRef } from 'overlayscrollbars-vue'

const { section = '', type, orientation = 'vertical' } = defineProps<{
  section?: string
  orientation?: 'horizontal' | 'vertical'
  type?: ('media' | 'course' | 'trail')[]
}>()

const ls = useLearningStore()

const scroller = ref<OverlayScrollbarsComponentRef | null>(null)
const canScrollLeft = ref(false)
const canScrollRight = ref(false)

// Elemento que de fato rola (o OverlayScrollbars cria um viewport interno)
const viewport = () => scroller.value?.osInstance()?.elements().viewport as HTMLElement | undefined

// Chamado pelos eventos do OverlayScrollbars (init, mudança de conteúdo/tamanho e scroll)
const updateArrows = () => {
  const el = viewport()
  if (!el) return

  canScrollLeft.value = el.scrollLeft > 1
  canScrollRight.value = Math.ceil(el.scrollLeft + el.clientWidth) < el.scrollWidth
}

// Avança/volta quase uma "tela" de cards, deixando um card como referência
const scrollList = (direction: -1 | 1) => {
  const el = viewport()
  if (!el) return

  el.scrollBy({ left: direction * Math.max(el.clientWidth - 80, 260), behavior: 'smooth' })
}

const list = computed(() => {
  const items = ls.activeItemRelated

  const result = items.filter(item => {
    if (Array.isArray(type) && type.length > 0) {
      return type.includes(item.type)
    }
    return true
  }).map((item) => {
    const data = ls.cards[item.type]?.find(c => c.id === item.id)

    return {
      id: item.id,
      name: data.title,
      type: data.type,
      mediaType: data.mediaType,
      progress: data.progress || 50,
      enrolledAt: data.enrolledAt || null,
      img: data.images.horizontal || data.images.vertical,
      workload: data.workload || 0,
      description: data.description || ''
    }
  })

  // Ordena por progresso (itens já finalizados por último)
  result.sort((a, b) => {
    if (a.progress === 100 && b.progress !== 100) return 1
    if (a.progress !== 100 && b.progress === 100) return -1
    return 0
  })

  return result
})

// Converte segundos para hh:mm:ss
const secondsToTime = (seconds: number) => {
  if (!seconds) return '0s'
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60

  if (!hours && !minutes && secs) {
    return `${secs}s`
  }

  return [
    hours.toString().padStart(2, '0'),
    minutes.toString().padStart(2, '0')
  ].join(':')
}

// Reproduz o próximo conteúdo da lista
const playNext = () => {
  if (list.value.length === 0) return

  const currentIndex = list.value.findIndex(i => i.id === ls.activeItem?.id && i.type === ls.activeItem?.type)
  let nextIndex = currentIndex + 1

  if (nextIndex >= list.value.length) {
    nextIndex = 0 // Volta para o primeiro item se estiver no último
  }

  const nextItem = list.value[nextIndex]
  if (nextItem) {
    ls.viewItem(nextItem.id, nextItem.type, section)
  }
}

defineExpose({
  playNext,
  qtdItems: () => list.value.length
})
</script>

<style scoped lang="scss">
.nav-arrow {
  @apply h-9 w-9 flex items-center justify-center rounded-full border transition-colors;
  @apply border-[--c-primary-700] text-[--c-primary-700] hover:bg-[--c-primary-300];
  @apply dark:border-[--c-primary-300] dark:text-[--c-primary-300] dark:hover:bg-[--c-primary-700];

  &:disabled {
    @apply opacity-40 cursor-not-allowed;
    background-color: transparent;
  }
}

.little-card {
  position: relative;
  width: 260px;
  max-width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 4px;
  overflow: hidden;

  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  .fallback-cover {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom right, var(--c-primary-600), var(--c-primary-700));
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.3s ease;
  }

  &:hover {
    > img, > .fallback-cover {
      transform: scale(1.05);
    }
  }
}

*::-webkit-scrollbar {
  width: 8px;
  transform: translateX(-10px);
}

*::-webkit-scrollbar-track {
  @apply bg-[#e5e5e5] dark:bg-neutral-700;
  border-radius: 6px;
  margin-right: 10px; 
}

*::-webkit-scrollbar-thumb {
  background-color: #a8a8a8;
  border-radius: 6px;
}
</style>